/**
 *
 * ChannelActionsMenu
 *
 * The per-row three-dots (⋯) actions menu for a channel. Owns the confirm-modal
 * state for the two destructive/semi-destructive actions:
 *
 *   - Delete  — irreversible. Cascades the channel's gifs/images/faces/shares/
 *               leads/downloads on the server. Gated behind a HARD confirm: the
 *               owner must type the channel's exact name before the danger
 *               button enables.
 *   - Archive — reversible. Sets `archived: true` (the channel moves out of the
 *               default `active` view; it reappears under the `archived` status
 *               filter). A lighter single-step confirm.
 *
 * Authorization is server-authoritative. The rails ChannelMapper emits two
 * per-channel capability flags on the v2/channels list:
 *
 *   - can_delete  == ChannelPolicy#destroy? (owner OR super_admin)
 *   - can_archive == ChannelPolicy#update?  (owner OR super_admin OR channel_admin)
 *
 * We render each action ONLY when its flag is truthy. We do NOT re-derive authz
 * client-side. super_admin is OR-ed in as a belt-and-suspenders override so the
 * superadmin retains delete even on a payload that (for any reason) omits the
 * flag. SAFE DEFAULT: when a flag is absent/undefined — e.g. before the rails
 * #1084 backend deploys the flags — the action stays HIDDEN, so we never show a
 * control the API would reject.
 */

import React, { useState } from 'react';
import { t } from 'copy/t';
import { Link } from 'react-router-dom';
import Popover from 'components/Popover';
import Icon from 'components/Icon';
import Modal from 'components/Modal';
import {
  LinkedlistItems,
  LinkedlistItem,
} from 'components/Popover/styledComponents';
import {
  ModalBody,
  ModalActions,
  CancelButton,
  DangerButton,
  ArchiveButton,
  ConfirmInput,
} from './styledComponents';

const ChannelActionsMenu = ({
  id,
  name,
  url,
  slug,
  permissionRole,
  canDelete,
  canArchive,
  deleteChannel,
  archiveChannel,
  channelManagementBlocked,
  openResubscribeModal,
}) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [confirmName, setConfirmName] = useState('');

  const isSuperAdmin = permissionRole === 'super_admin';
  // Safe-default-hidden: only show when the server says so (super_admin OR-ed
  // in). An absent/undefined flag → false → item hidden.
  const showDelete = isSuperAdmin || canDelete === true;
  const showArchive = isSuperAdmin || canArchive === true;

  // The hard-confirm gate: the typed name must match the channel name exactly.
  const nameMatches = confirmName.trim() === name;

  const closeDelete = () => {
    setDeleteOpen(false);
    setConfirmName('');
  };

  const handleConfirmDelete = () => {
    // Defensive guard: the danger button is already disabled until the typed
    // name matches, but re-assert the invariant here so the irreversible call
    // can never fire without an exact-name confirmation.
    if (!nameMatches) return;
    deleteChannel(id);
    closeDelete();
  };

  const handleConfirmArchive = () => {
    archiveChannel(id);
    setArchiveOpen(false);
  };

  // hrefs must be absolute: under react-router v7 a relative `to` resolves
  // against the matched ROUTE path (here `/channels`), not the URL the way
  // history v4 did — `channels/1/edit` would navigate to
  // `/channels/channels/1/edit` and land on the 404 catch-all.
  // Only EDIT is gated when the subscription has ended. Viewing is not: the
  // gallery and the leads are the account's own record of events
  // that already happened, and withholding them would be taking something away
  // rather than closing a control. Archive and delete are likewise untouched —
  // they are the server's call (can_archive/can_delete above), and a lapsed
  // account tidying up is not a thing to block.
  //
  // And the account-wide flag is not the last word on edit either. `canArchive`
  // is the per-channel EDIT verdict under a legacy name: rails derives it from
  // `ChannelPolicy#update?` because archiving IS a channel update, so it
  // already carries the exemption the account-wide flag cannot express — a
  // channel_admin may edit channels they administer for a client even when
  // their own subscription has ended. Strict `!== true` keeps an absent flag
  // blocked; an entitled account never reaches the expression.
  const editIsBlocked = channelManagementBlocked && canArchive !== true;
  const links = [
    editIsBlocked
      ? { title: 'edit channel', onClick: openResubscribeModal }
      : { title: 'edit channel', href: '/channels/' + id + '/edit' },
    { title: 'View Gallery', onClick: () => window.open(slug ? `/ch/${slug}` : url) },
    { title: 'View Leads', href: '/leads/' + id },
    showArchive
      ? { title: 'archive channel', onClick: () => setArchiveOpen(true) }
      : null,
    showDelete
      ? { title: 'delete channel', style: 'danger', onClick: () => setDeleteOpen(true) }
      : null,
  ].filter((x) => x);

  return (
    <div data-no-row-click>
      <Popover triggerLabel="Channel actions">
        <Icon name="menu" padding="14px 9px" />
        <LinkedlistItems>
          {links.map((link, i) => (
            <Link
              key={i}
              to={link.href ? link.href : ''}
              onClick={(e) => {
                if (link.onClick) {
                  e.preventDefault();
                  link.onClick();
                }
              }}
            >
              <LinkedlistItem className={link.style ? link.style : null}>
                <div>{link.title}</div>
              </LinkedlistItem>
            </Link>
          ))}
        </LinkedlistItems>
      </Popover>

      {deleteOpen && (
        <Modal title="Delete channel" close={closeDelete}>
          <ModalBody>
            <p>
              You're about to delete <strong>{name}</strong>. Its gallery, photo
              pages and leads go with it.
            </p>
            <p>
              <strong>This cannot be undone.</strong> To confirm, type the
              channel name <strong>{name}</strong> below.
            </p>
            <ConfirmInput
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={t("placeholders.channelNameConfirm")}
              aria-label="Type the channel name to confirm deletion"
              autoFocus
            />
            <ModalActions>
              <CancelButton type="button" onClick={closeDelete}>
                Cancel
              </CancelButton>
              <DangerButton
                type="button"
                onClick={handleConfirmDelete}
                disabled={!nameMatches}
              >
                Delete channel
              </DangerButton>
            </ModalActions>
          </ModalBody>
        </Modal>
      )}

      {archiveOpen && (
        <Modal title="Archive this channel?" close={() => setArchiveOpen(false)}>
          <ModalBody>
            <p>
              Archive <strong>{name}</strong>? It moves out of your active
              channels. You can find it under the <strong>archived</strong>
              {' '}filter. To restore it, contact us.
            </p>
            <ModalActions>
              <CancelButton type="button" onClick={() => setArchiveOpen(false)}>
                Cancel
              </CancelButton>
              <ArchiveButton type="button" onClick={handleConfirmArchive}>
                Archive channel
              </ArchiveButton>
            </ModalActions>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
};

export default ChannelActionsMenu;
